<template>
  <v-card class="mt-5">
    <v-card-title class="contracts-table__header">
      <div class="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
        <div class="flex min-w-0 items-center gap-2">
          <span>{{ companyLabels.Plural('agreement') }}</span>
          <span class="text-sm text-medium-emphasis">{{ envelopesTotal }}</span>
        </div>
        <v-spacer class="hidden sm:block"/>
        <v-btn
          :to="{hash: '#templates'}"
          class="w-full sm:w-auto"
          data-hint="contracts-templates-button"
          size="small"
          variant="tonal"
        >
          <v-icon size="small" start>mdi-file-cog-outline</v-icon>
          Templates{{ validTemplatesCount > 0 ? ` (${validTemplatesCount})` : '' }}
        </v-btn>
        <v-text-field
          v-model="searchQuery"
          :placeholder="`Search ${companyLabels.plural('customer')}`"
          class="contracts-table__search"
          clearable
          density="compact"
          hide-details
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
        />
      </div>
    </v-card-title>
    <div class="px-4">
      <v-chip-group v-model="statusFilter" mandatory>
        <v-chip
          v-for="option in statusFilterOptions"
          :key="option.value"
          :value="option.value"
          filter
          size="small"
        >
          {{ option.title }}
        </v-chip>
      </v-chip-group>
    </div>
    <v-card-text>
      <div class="">
        <v-data-table-server
          v-model:options="dataTableOptions"
          :headers="envelopesHeaders"
          :items="envelopes"
          :items-length="envelopesTotal"
          :items-per-page="dataTableOptions.itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          :items-per-page-text="`${companyLabels.Plural('agreement')} per page`"
          :loading="envelopeLoading.envelopes"
          :mobile-breakpoint="0"
          class="contracts-table"
        >
          <template v-slot:body="{ items }: { items: readonly any[] }">
            <tr v-if="items.length === 0 && !envelopeLoading.envelopes">
              <td class="py-10 text-center text-medium-emphasis" colspan="6">
                <div v-if="hasActiveFilters">
                  No contracts match your filters.
                  <v-btn class="ml-1" size="small" variant="text" @click="clearFilters">Clear filters</v-btn>
                </div>
                <div v-else>
                  No contracts yet. They're created automatically when a booking uses a document template.
                </div>
              </td>
            </tr>
            <tr
              v-for="(item, index) in items.map(resolveEnvelope)"
              :key="item?.uuid ?? index"
              class="contracts-table__row border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200"
            >
              <th class="contracts-table__cell contracts-table__cell--name font-medium text-gray-900 whitespace-nowrap dark:text-white" scope="row">
                <div v-for="(recipient, key) in recipients(item)" :key="key">
                  <router-link v-if="renterId(recipient)" :to="{name: 'customers.single', params: { r_id: renterId(recipient)}}">
                    {{ recipient.name }}
                  </router-link>
                </div>
              </th>
              <td class="contracts-table__cell">
                <div class="contracts-table__cell-content contracts-table__cell-content--stacked">
                  <v-chip :color="envelopeStatusText(item).color" class="text-white" size="x-small">
                    {{ envelopeStatusText(item).text }}
                  </v-chip>
                  <div v-if="item.completed_at">{{ formatRentalDate(item.completed_at) }}</div>
                </div>
              </td>
              <td class="contracts-table__cell">
                <div class="contracts-table__cell-content">
                  <v-btn v-if="item.file_path" v-tippy :href="item.file_path" content="Signed document - Open in new window" size="small" target="_blank" variant="text">
                    <v-icon size="small">mdi-file-check-outline</v-icon>
                  </v-btn>
                  <v-btn v-else v-tippy :href="item.pdf_url" class="text-medium-emphasis" content="Not yet signed - Open template in new window" size="small" target="_blank" variant="text">
                    <v-icon size="small">mdi-file-clock-outline</v-icon>
                  </v-btn>
                </div>
              </td>
              <td class="contracts-table__cell">
                <div class="contracts-table__cell-content">
                  <span v-if="item.attachments.length === 0"></span>
                  <v-menu
                    v-else
                    v-model="attachmentsMenus[item.uuid]"
                    :close-on-content-click="false"
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        variant="text"
                      >
                        {{ item.attachments.length }}
                      </v-btn>
                    </template>

                    <v-card max-width="300">
                      <v-list density="compact">
                        <template v-for="attachment in item.attachments" :key="attachment.uuid">
                          <v-list-item v-if="attachmentImage(attachment)" tile>
                            <img :src="documentThumb(attachmentImage(attachment))" alt="Attachment Image" class="border" loading="lazy">
                          </v-list-item>
                          <v-list-item v-else tile>
                            <template v-slot:prepend>
                              <v-avatar
                                color="primary"
                                size="25"
                              >
                                <v-icon size="sma">
                                  mdi-file-outline
                                </v-icon>
                              </v-avatar>
                            </template>

                            <template v-slot:append>
                              <v-btn :href="attachment.url" target="_blank" variant="text">
                                <v-icon size="small">mdi-open-in-new</v-icon>
                              </v-btn>
                            </template>

                            <template v-slot:subtitle>
                              {{ attachment.attachment_id }}
                            </template>

                            <template v-slot:title>
                              {{ attachment.name }}
                            </template>
                          </v-list-item>


                        </template>
                      </v-list>
                    </v-card>
                  </v-menu>
                </div>
              </td>
              <td class="contracts-table__cell">
                <div class="contracts-table__cell-content">
                  <span v-if="item.created_at">{{ formatRentalDate(item.created_at) }}</span>
                </div>
              </td>
              <td class="contracts-table__cell">
                <div class="contracts-table__cell-content contracts-table__cell-content--actions">
                  <v-menu>
                    <template v-slot:activator="{ props }">
                      <v-btn
                        size="small"
                        v-bind="props"
                        variant="text"
                      >
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item v-if="item.file_path" @click="openInNewTab(item.file_path)">
                        <v-list-item-title>
                          <v-icon class="mr-1" size="small">mdi-download</v-icon>
                          Download PDF
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item v-else @click="openInNewTab(item.pdf_url)">
                        <v-list-item-title>
                          <v-icon class="mr-1" size="small">mdi-download</v-icon>
                          Download Template Used
                        </v-list-item-title>
                      </v-list-item>
                      <!--                      <v-list-item>-->
                      <!--                        <v-list-item-title>-->
                      <!--                          <v-icon size="small" class="mr-1">mdi-eye</v-icon>-->
                      <!--                          Preview-->
                      <!--                        </v-list-item-title>-->
                      <!--                      </v-list-item>-->
                      <v-list-item :disabled="!hasPendingSigners(item) || envelopeVoided(item)" @click="openReminderDialog(item)">
                        <v-list-item-title>
                          <v-icon class="mr-1" size="small">mdi-email-outline</v-icon>
                          Send Reminder
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item :disabled="envelopeVoided(item)" @click="openVoidEnvelopeDialog(item)">
                        <v-list-item-title>
                          <v-icon class="mr-1" size="small">mdi-close-circle-outline</v-icon>
                          Void
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="openEnvelopInfoDialog(item)">
                        <v-list-item-title>
                          <v-icon class="mr-1" size="small">mdi-information-outline</v-icon>
                          View Info
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table-server>
      </div>
    </v-card-text>
  </v-card>

  <v-dialog v-model="voidEnvelopeDialog" max-width="400">
    <v-card :loading="envelopeLoading.voiding">
      <v-card-title class="text-h5">Are you sure?</v-card-title>
      <v-divider/>
      <v-card-text>
        Are you sure you want to void this envelope?

        <v-alert v-if="envelopeForm.errors.any()" class="mt-5" color="red" density="compact">
          <strong>There was an error:</strong>
          {{ envelopeForm.errors.flatten()[0] }}
        </v-alert>
      </v-card-text>

      <v-divider/>

      <v-card-actions>
        <v-spacer/>

        <v-btn :disabled="envelopeLoading.voiding" size="small" variant="text" @click="voidEnvelopeDialog = false; envelopeForm.errors.clear();">
          {{ $t('nevermind') }}
        </v-btn>

        <v-btn :disabled="envelopeLoading.voiding || envelopeForm.errors.any()" color="error" @click="voidEnvelopeCommit(currentWorkingEnvelope)">
          Void
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="reminderDialog" aria-label="Send Reminder" max-width="600">
    <v-card :disabled="envelopeLoading.reminder" :loading="envelopeLoading.reminder">
      <v-card-title>Send Reminder</v-card-title>
      <v-divider/>
      <v-card-text>
        <div class="my-2">Choose which pending signers should receive a reminder.</div>
        <v-checkbox
          v-for="(reminder, index) in reminderData.recipients"
          :key="index"
          v-model="reminder.send"
          :label="reminder.label"
          hide-details
        />

        <v-alert
          v-if="envelopeForm.errors.any()"
          class="mt-5"
          color="red"
          density="compact"
        >
          <v-alert-title>Error</v-alert-title>

          {{ envelopeForm.errors.flatten()[0] }}
        </v-alert>
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-spacer/>
        <v-btn :disabled="envelopeLoading.reminder" size="small" variant="text" @click="reminderDialog = false">
          {{ $t('nevermind') }}
        </v-btn>
        <v-btn :disabled="sendReminderReady" :loading="envelopeLoading.reminder" color="primary" variant="tonal" @click="sendReminderInit()">
          Send
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="envelopInfoDialog" max-width="600">
    <v-card :disabled="documentsLoading.saving" :loading="documentsLoading.saving">
      <v-card-title>Info</v-card-title>
      <v-card-text>
        <div class="mb-5">
          <v-chip :color="envelopeStatusText(currentWorkingEnvelope).color" size="x-small">
            <span>{{ envelopeStatusText(currentWorkingEnvelope).text }}</span>
          </v-chip>
        </div>

        <div>
          <div v-for="recipient in currentWorkingEnvelopeRecipients" :key="recipient.uuid">
            <div class="flex">
              <div v-if="recipient.completed_at">
                <v-icon v-tippy :content="`Signed at ${recipient.completed_at}`" class="mr-1" size="small">mdi-check
                </v-icon>
              </div>
              <div v-else-if="recipient.opened_at">
                <v-icon v-tippy :content="`Opened at ${recipient.opened_at}`" class="mr-1" size="small">mdi-eye
                </v-icon>
              </div>
              <div v-else>
                <v-icon v-tippy :content="`Waiting`" class="mr-1" size="small">mdi-clock-outline</v-icon>
              </div>
              <div>
                {{ recipient.renter.full_name }}
                ({{ recipient.recipient }})
              </div>
            </div>
          </div>
        </div>

        <div>
          <div v-for="(signature, key) in envelopeRecipientSignatures(currentWorkingEnvelope)" :key="key">
            <div v-if="signature.signature" class="border rounded my-1 p-1">
              <v-menu
                v-if="signature.signature"
                v-model="signature.largerSignature"
                :close-on-content-click="false"
                open-on-hover
              >
                <template v-slot:activator="{ props }">
                  <v-img :src="signature.signature" height="50" v-bind="props" width="100"></v-img>
                  <small>{{ signature.name }}</small>
                </template>

                <v-card>
                  <v-card-text>
                    <v-img :src="signature.signature" height="200"></v-img>
                    <small>{{ signature.name }}</small>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn :disabled="documentsLoading.saving" size="small" variant="text" @click="envelopInfoDialog = false">
          {{ $t('okay') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import {computed, reactive, ref, watch} from 'vue';
import {documentThumb} from '@/utils/twicpics';
import {useEnvelope} from '@/composables/useEnvelope';
import {useTemplate} from '@/composables/useTemplate';
import {envelopeStatusLabel} from '@/utils/envelopeStatus';
import {statusVuetifyColor} from '@/utils/statusColors';
import {useStore} from 'vuex';
import {useDateFormatter} from '@/composables/useDate';
import type {EnvelopeInterface} from '@/store/modules/envelope';
import type {RenterInterface} from '@/types/user';
import {useCompanyLabels} from '@/composables/useCompanyLabels';

const store = useStore();
const companyLabels = useCompanyLabels();
const {
  envelopeForm,
  envelopeLoading,
  envelopes,
  envelopesPagination,
  envelopesQuery,
  sendEnvelopeReminder,
  voidEnvelope,
  getEnvelopes,
  updateCustomerQuery,
} = useEnvelope();
const {formatRentalDate} = useDateFormatter();

const documentsLoading = computed(() => store.getters['document/loading']);

const {documentTemplates} = useTemplate();
const validTemplatesCount = computed(() => documentTemplates.value.filter((template) => template.documents).length);

const envelopesTotal = computed(() => envelopesPagination.value.total);

const currentWorkingEnvelope = ref<EnvelopeInterface | null>(null);
const envelopInfoDialog = ref(false);
const voidEnvelopeDialog = ref(false);
const reminderDialog = ref(false);

const resolveEnvelope = (row: any): EnvelopeInterface => {
  return (row?.raw ?? row) as EnvelopeInterface;
};

const currentWorkingEnvelopeRecipients = computed(() => {
  return currentWorkingEnvelope.value?.recipients ?? [];
});

const dataTableOptions = ref({
  page: 1,
  itemsPerPage: 25,
  sortBy: ['created_at'],
  sortDesc: [true],
  status: 'all',
});

const searchQuery = ref('');
const statusFilter = ref('all');
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const statusFilterOptions = [
  {title: 'All', value: 'all'},
  {title: 'Pending signature', value: 'pending signature'},
  {title: 'Completed', value: 'completed'},
  {title: 'Voided', value: 'voided'},
];

const hasActiveFilters = computed(() => {
  return Boolean(searchQuery.value) || statusFilter.value !== 'all';
});

const applyFilters = () => {
  updateCustomerQuery({key: 'page', value: 1});
  updateCustomerQuery({
    key: 'filter',
    value: {
      ...envelopesQuery.value.filter,
      search: searchQuery.value ?? '',
      status: statusFilter.value === 'all' ? [] : [statusFilter.value],
    },
  });
  getEnvelopes();
};

const clearFilters = () => {
  searchQuery.value = '';
  statusFilter.value = 'all';
};

watch(searchQuery, () => {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
  searchDebounceTimer = setTimeout(applyFilters, 400);
});

watch(statusFilter, () => {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = null;
  }
  applyFilters();
});
const envelopeVoided = (envelope: EnvelopeInterface) => {
  return envelope.status === 'voided';
};

const sendReminderReady = computed(() => {
  return reminderData.value.recipients.length === 0 || reminderData.value.recipients.every(r => !r.send);
});

const itemsPerPageOptions = [
  {value: 10, title: '10'},
  {value: 25, title: '25'},
  {value: 50, title: '50'},
  {value: 100, title: '100'},
  {value: -1, title: '$vuetify.dataFooter.itemsPerPageAll'}
];

const envelopesHeaders = computed(() => [
  {title: companyLabels.value.Plural('customer'), key: 'renter', sortable: false},
  {title: 'Status', key: 'status', sortable: false},
  // { text: 'Opened', value: 'opened', width: 100 },
  // { text: 'Completed', value: 'completed_at', width: 200 },
  // { text: 'Signatures', value: 'signature' },
  {title: 'Files', key: 'pdf_url', sortable: false},
  {title: 'Attachments', key: 'attachments', width: 125, sortable: false},
  {title: 'Created', key: 'created_at', width: 200},
  {title: '', sortable: false, key: 'uuid'},
]);

type ReminderRecipient = {
  email: string;
  label: string;
  send: boolean;
};

const reminderData = ref<{ recipients: ReminderRecipient[] }>({
  recipients: [],
});

const attachmentsMenus = reactive<Record<string, boolean>>({});

watch(() => dataTableOptions.value.page, (newPage) => {
  updateCustomerQuery({key: 'page', value: newPage});
  getEnvelopes();
});

watch(() => dataTableOptions.value.itemsPerPage, (newItemsPerPage) => {
  updateCustomerQuery({key: 'itemsPerPage', value: newItemsPerPage});
  getEnvelopes();
});

watch(() => dataTableOptions.value.sortBy, (newSort) => {
  updateCustomerQuery({key: 'sortBy', value: [newSort?.[0]?.['key']]});
  updateCustomerQuery({key: 'sortDesc', value: [newSort?.[0]?.['order'] === 'desc']});
  getEnvelopes();
});

//watch reminderDialog and when it closes, reset the form
watch(reminderDialog, (val) => {
  if (!val) {
    envelopeForm.value.errors.clear();
  }
})

watch(dataTableOptions, (val) => {
  // if there is no status filter, then we need to reset the page to 1
  if (!val.status) {
    dataTableOptions.value.status = 'all';
  }
});

const recipients = (envelope: EnvelopeInterface) => {
  // Check if there are no recipients in the envelope
  if (!envelope.recipients?.length) {
    return [{
      name: renterFullName(envelope.renter),
      r_id: envelope.renter?.r_id,
    }];
  }

  // Process each recipient to extract their details
  return envelope.recipients.map(recipient => ({
    name: renterFullName(recipient.renter),
    r_id: recipient.renter?.r_id,
  }));
};

const renterFullName = (renter: RenterInterface) => {
  const name = renter?.full_name;
  if (name) {
    return name;
  }
  return `${renter?.first_name} ${renter?.last_name}`.trim();
};

const renterId = (renter: { name: string, r_id: string }) => {
  return renter.r_id;
};


const envelopeStatusText = (envelope: EnvelopeInterface | null) => {
  if (!envelope) {
    return {text: 'Unknown', color: statusVuetifyColor(null)};
  }
  if (['init', 'pending', 'in_progress', 'declined', 'voided', 'expired'].includes(envelope.status)) {
    return {text: envelopeStatusLabel(envelope.status), color: statusVuetifyColor(envelope.status)};
  }
  if (envelope.recipients?.some(r => !r.completed_at)) return {
    text: 'Pending signature',
    color: statusVuetifyColor('pending')
  };
  if (envelope.completed_at) return {text: 'Completed', color: statusVuetifyColor('completed')};
  return {text: envelope.status, color: statusVuetifyColor(envelope.status)};
};

const envelopeRecipientSignatures = (envelope: EnvelopeInterface | null) => {
  if (!envelope) {
    return [];
  }
  // Check if there are no recipients in the envelope
  if (!envelope.recipients?.length) {
    return [{
      signature: envelope.signature,
      recipient: envelope.renter?.full_name ?? 'Unknown Renter',
      name: envelope.renter?.full_name ?? 'Unknown Renter',
      largerSignature: false,
    }];
  }

  // Process each recipient to extract signature information
  return envelope.recipients.map(recipient => ({
    signature: recipient.signature,
    recipient: recipient.recipient,
    name: recipient.renter?.full_name ?? 'Unknown Renter',
    largerSignature: false,
  }));
};

const attachmentImage = (attachment: unknown) => {
  const a = attachment as { document_images?: string[] } | null;
  return a?.document_images?.[0];
};

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noreferrer');
};

const hasPendingSigners = (envelope: EnvelopeInterface) => {
  return pendingSigners(envelope).length > 0;
};

const pendingSigners = (envelope: EnvelopeInterface) => {
  return envelope.recipients.filter((r) => !r.completed_at);
};
const openEnvelopInfoDialog = (envelope: EnvelopeInterface) => {
  currentWorkingEnvelope.value = envelope;
  envelopInfoDialog.value = true;
};

const openVoidEnvelopeDialog = (envelope: EnvelopeInterface) => {
  currentWorkingEnvelope.value = envelope;
  voidEnvelopeDialog.value = true;
};


const openReminderDialog = (envelope: EnvelopeInterface) => {
  currentWorkingEnvelope.value = envelope;

  const pendingSignersData = pendingSigners(envelope);

  if (pendingSignersData.length === 0) {
    return;
  }

  reminderData.value.recipients = pendingSignersData.map(signer => ({
    email: signer.recipient,
    label: `${signer.renter?.full_name ?? 'Unknown Name'} (${signer.recipient})`,
    send: true,
  }));

  reminderDialog.value = true;
};

const voidEnvelopeCommit = async (envelope: EnvelopeInterface | null) => {
  if (!envelope) {
    return;
  }
  await voidEnvelope(envelope);
  voidEnvelopeDialog.value = false;
  currentWorkingEnvelope.value = null;

  // Wait before fetching new data
  setTimeout(() => getEnvelopes(), 300);
};

const sendReminderInit = () => {
  reminderData.value.recipients.forEach((r) => {
    if (r.send) {
      sendEnvelopeReminder({
        envelope: currentWorkingEnvelope.value as EnvelopeInterface,
        email: r.email,
      }).then(() => {
        reminderDialog.value = false;
      }).catch((error) => {
        console.dir(error);
      })
    }
  });
};
</script>

<style scoped>
.contracts-table :deep(.contracts-table__cell) {
  height: 56px;
  padding: 10px 24px;
  vertical-align: middle;
}

.contracts-table :deep(.contracts-table__cell--name) {
  text-align: left;
}

.contracts-table :deep(.contracts-table__cell-content) {
  align-items: center;
  display: inline-flex;
  min-height: 36px;
}

.contracts-table :deep(.contracts-table__cell-content--stacked) {
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
}

.contracts-table :deep(.contracts-table__cell-content--actions) {
  display: flex;
  justify-content: flex-end;
}

.contracts-table__search {
  max-width: 100%;
}

@media (min-width: 640px) {
  .contracts-table__search {
    max-width: 280px;
  }
}
</style>
